// Application-level error boundary.
//
// A rendering crash is a bug in the page, not a problem with the session, so
// the session is left untouched: the user gets a Nexploy error screen with
// "Reload page" and "Go to home" instead of React unmounting everything into
// a blank white page. A short reference is shown and logged together with the
// error so a report from the user can be matched to the console/log entry.
//
// Mounted twice in App.js: once inside the providers/router (themed, and it
// resets itself when the route changes), and once around everything as the
// last line of defence (it then reads theme/language from storage).
import React from "react";
import { AlertTriangle, Home, RotateCw } from "lucide-react";
import { sessionManager } from "../../utils/sessionManager";
import { getRoleHomePath } from "../../utils/roleHome";
import {
  useSessionUi,
  SessionScreenFrame,
  SessionCard,
  CardTitle,
  CardBody,
  ReferenceLine,
  IconTile,
} from "./SessionUi";

const makeReference = () =>
  `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

const currentRole = () => {
  try {
    return sessionManager.getUserRole() || localStorage.getItem("role");
  } catch (err) {
    return null;
  }
};

export function AppErrorScreen({ reference }) {
  const ui = useSessionUi();
  const { palette, t } = ui;

  // Full page loads: after a crash the in-memory state may be inconsistent,
  // and storage (the session) survives a reload.
  const reload = () => window.location.reload();
  const goHome = () => window.location.assign(getRoleHomePath(currentRole()) || "/login");

  return (
    <SessionScreenFrame ui={ui} label={t.crashTitle}>
      <SessionCard ui={ui}>
        <div role="alert">
          <IconTile icon={AlertTriangle} tone="danger" palette={palette} />
          <CardTitle ui={ui}>{t.crashTitle}</CardTitle>
          <CardBody ui={ui}>{t.crashBody}</CardBody>
        </div>
        <div className="nx-session-actions" style={{ marginTop: 24 }}>
          <button type="button" className="nx-session-btn nx-session-btn--secondary" onClick={goHome}>
            <Home size={16} aria-hidden="true" />
            {t.goHome}
          </button>
          <button type="button" className="nx-session-btn nx-session-btn--primary" onClick={reload} autoFocus>
            <RotateCw size={16} aria-hidden="true" />
            {t.reload}
          </button>
        </div>
        <ReferenceLine ui={ui} label={t.errorReference} value={reference} />
      </SessionCard>
    </SessionScreenFrame>
  );
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reference: null };
  }

  static getDerivedStateFromError(error) {
    return { error, reference: makeReference() };
  }

  componentDidCatch(error, info) {
    // console.error is kept in production builds (see index.js).
    console.error(`[Nexploy] Rendering error ${this.state.reference}:`, error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Navigating elsewhere (e.g. browser Back) gets a fresh attempt.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, reference: null });
    }
  }

  render() {
    if (this.state.error) return <AppErrorScreen reference={this.state.reference} />;
    return this.props.children;
  }
}
